import { useEffect, useState } from "react";
import { useI18n } from "../i18n";
import { LangSwitch } from "../components/LangSwitch";
import { Home } from "../pages/Home";
import { Organisation } from "../pages/Organisation";
import { Members } from "../pages/Members";
import { ModulesPage } from "../pages/ModulesPage";
import { ComingSoon } from "../pages/ComingSoon";
import { Employees } from "../pages/Employees";
import { EmployeeProfile } from "../pages/EmployeeProfile";
import { Documents } from "../pages/Documents";
import { Settings } from "../pages/Settings";
import { Leave, MyLeave } from "../pages/Leave";
import { Attendance } from "../pages/Attendance";
import { Approvals } from "../pages/Approvals";
import { Team } from "../pages/Team";
import { Letters, MyLetters } from "../pages/Letters";
import { Policies } from "../pages/Policies";
import { Dashboard } from "../pages/Dashboard";
import { MyRequests } from "../pages/MyRequests";
import { Alerts } from "../pages/Alerts";
import { Payroll } from "../pages/Payroll";
import { MyPayslips } from "../pages/MyPayslips";
import { Overtime } from "../pages/Overtime";
import { Loans } from "../pages/Loans";
import { Rewards } from "../pages/Rewards";
import { Expenses } from "../pages/Expenses";
import { Assets } from "../pages/Assets";
import { Offboarding } from "../pages/Offboarding";
import { Recruitment } from "../pages/Recruitment";
import { Onboarding } from "../pages/Onboarding";
import { Performance } from "../pages/Performance";
import { Training } from "../pages/Training";
import { Relations } from "../pages/Relations";
import { Engagement } from "../pages/Engagement";
import { Analytics } from "../pages/Analytics";
import type { Workspace } from "../types";

interface Props {
  ws: Workspace;
  onSignOut: () => void;
  onModulesChanged: () => void;
}

interface NavItem {
  id: string;
  label: string;
  show: boolean;
}

/** Modules whose screens exist; they get a Workspace link instead of a placeholder. */
const BUILT = new Set(["documents", "hiring_documents", "leave", "attendance", "approvals", "self_service", "alerts", "letters", "policies", "dashboards", "payroll", "overtime", "loans", "variable_pay", "benefits", "expenses", "assets", "offboarding", "recruitment", "onboarding", "performance", "training", "employee_relations", "engagement", "analytics"]);

function currentPage(): string {
  const h = window.location.hash.replace(/^#/, "");
  return h || "home";
}

export function Shell({ ws, onSignOut, onModulesChanged }: Props) {
  const { t, pick } = useI18n();
  const [page, setPage] = useState(currentPage);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onHash = () => setPage(currentPage());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const can = (p: string) => ws.permissions.has(p);
  const on = (m: string) => ws.enabled.has(m);
  const linked = !!ws.membership.employment_id;
  const self = on("self_service") && linked;
  // "Me": the signed-in person's own things (self-service)
  const me: NavItem[] = [
    { id: "myfile", label: t("navMyFile"), show: self },
    { id: "myleave", label: t("navMyLeave"), show: self && on("leave") },
    { id: "myletters", label: t("navMyLetters"), show: self && on("letters") },
    { id: "mypayslips", label: t("navMyPayslips"), show: self && on("payroll") },
    { id: "myrequests", label: t("navMyRequests"), show: self },
    { id: "team", label: t("navTeam"), show: on("self_service") && ws.teamSize > 0 && !can("employees.read") },
  ];
  const core: NavItem[] = [
    { id: "home", label: t("navHome"), show: true },
    { id: "dashboard", label: t("navDashboard"), show: on("dashboards") && can("employees.read") },
    { id: "approvals", label: t("navApprovals"), show: on("approvals") && (can("leave.manage") || can("employees.manage") || can("letters.issue") || can("loans.manage") || can("attendance.manage") || can("expenses.manage") || ws.teamSize > 0) },
    { id: "alerts", label: t("navAlerts"), show: on("alerts") && (linked || can("employees.read") || ws.teamSize > 0) },
    { id: "employees", label: t("navEmployees"), show: can("employees.read") },
    { id: "leave", label: t("navLeave"), show: on("leave") && can("leave.read") },
    { id: "attendance", label: t("navAttendance"), show: on("attendance") && can("attendance.read") },
    { id: "overtime", label: t("navOvertime"), show: on("overtime") && can("attendance.read") },
    { id: "payroll", label: t("navPayroll"), show: on("payroll") && can("payroll.read") },
    { id: "loans", label: t("navLoans"), show: on("loans") && can("loans.read") },
    { id: "rewards", label: t("navRewards"), show: (on("variable_pay") || on("benefits")) && can("payroll.read") },
    { id: "expenses", label: t("navExpenses"), show: on("expenses") && (can("expenses.manage") || can("payroll.read")) },
    { id: "assets", label: t("navAssets"), show: on("assets") && can("assets.read") },
    { id: "recruitment", label: t("navRecruitment"), show: on("recruitment") && (can("recruitment.manage") || ws.teamSize > 0) },
    { id: "onboarding", label: t("navOnboarding"), show: on("onboarding") && (can("employees.read") || ws.teamSize > 0) },
    { id: "performance", label: t("navPerformance"), show: on("performance") && (can("performance.manage") || ws.teamSize > 0) },
    { id: "training", label: t("navTraining"), show: on("training") && can("training.manage") },
    { id: "relations", label: t("navRelations"), show: on("employee_relations") && (can("er.manage") || ws.teamSize > 0) },
    { id: "engagement", label: t("navEngagement"), show: on("engagement") && (linked || can("engagement.manage")) },
    { id: "analytics", label: t("navAnalytics"), show: on("analytics") && can("employees.read") },
    { id: "offboarding", label: t("navOffboarding"), show: on("offboarding") && can("employees.read") },
    { id: "letters", label: t("navLetters"), show: on("letters") && can("letters.issue") },
    { id: "policies", label: t("navPolicies"), show: on("policies") },
    { id: "documents", label: t("navDocuments"), show: on("documents") && can("documents.read") },
    { id: "organisation", label: t("navOrganisation"), show: can("org.read") },
    { id: "members", label: t("navMembers"), show: can("members.read") },
    { id: "modules", label: t("navModules"), show: can("modules.manage") },
    { id: "settings", label: t("navSettings"), show: can("settings.manage") },
  ];
  const moduleItems = ws.catalog
    .filter((m) => !m.always_on && ws.enabled.has(m.key) && !BUILT.has(m.key))
    .map((m) => ({ id: `m-${m.key}`, label: pick(m.name_en, m.name_ar), show: true }));

  const visible = [...me, ...core, ...moduleItems].filter((i) => i.show);
  const profileId = page.startsWith("employee-") ? page.slice("employee-".length) : null;
  const profileHome = can("employees.read") ? "employees" : ws.teamSize > 0 && on("self_service") ? "team" : null;
  const active = profileId && profileHome ? profileHome : visible.some((i) => i.id === page) ? page : "home";
  const company = ws.membership.company;

  function go(id: string) {
    window.location.hash = id;
    setPage(id);
    setMenuOpen(false);
  }

  function content() {
    if (profileId && profileHome && active === profileHome) return <EmployeeProfile ws={ws} employmentId={profileId} onBack={() => go(profileHome)} />;
    if (active === "approvals") return <Approvals ws={ws} onOpen={(id) => go(`employee-${id}`)} onGo={go} />;
    if (active === "alerts") return <Alerts ws={ws} onOpen={(id) => go(`employee-${id}`)} onGo={go} />;
    if (active === "payroll") return <Payroll ws={ws} />;
    if (active === "overtime") return <Overtime ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "rewards") return <Rewards ws={ws} />;
    if (active === "expenses") return <Expenses ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "assets") return <Assets ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "recruitment") return <Recruitment ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "onboarding") return <Onboarding ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "performance") return <Performance ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "training") return <Training ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "relations") return <Relations ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "engagement") return <Engagement ws={ws} />;
    if (active === "analytics") return <Analytics ws={ws} />;
    if (active === "offboarding") return <Offboarding ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "loans") return <Loans ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "mypayslips") return <MyPayslips ws={ws} />;
    if (active === "myrequests") return <MyRequests ws={ws} onGo={go} />;
    if (active === "team") return <Team onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "employees") return <Employees ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "documents") return <Documents ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "dashboard") return <Dashboard ws={ws} />;
    if (active === "letters") return <Letters ws={ws} />;
    if (active === "myletters") return <MyLetters ws={ws} />;
    if (active === "policies") return <Policies ws={ws} />;
    if (active === "organisation") return <Organisation ws={ws} />;
    if (active === "settings") return <Settings ws={ws} />;
    if (active === "leave") return <Leave ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "myleave" && ws.membership.employment_id) return <MyLeave ws={ws} />;
    if (active === "attendance") return <Attendance ws={ws} onOpen={(id) => go(`employee-${id}`)} />;
    if (active === "myfile" && ws.membership.employment_id) return <EmployeeProfile ws={ws} employmentId={ws.membership.employment_id} />;
    if (active === "members") return <Members ws={ws} />;
    if (active === "modules") return <ModulesPage ws={ws} onSaved={onModulesChanged} />;
    if (active.startsWith("m-")) {
      const mod = ws.catalog.find((m) => `m-${m.key}` === active);
      if (mod) return <ComingSoon module={mod} />;
    }
    return <Home ws={ws} onOpen={go} onChanged={onModulesChanged} />;
  }

  const navLink = (i: NavItem) => (
    <li key={i.id}>
      <a href={`#${i.id}`} className={i.id === active ? "current" : ""} aria-current={i.id === active ? "page" : undefined}
        onClick={(e) => { e.preventDefault(); go(i.id); }}>
        {i.label}
      </a>
    </li>
  );

  return (
    <div className="app">
      {menuOpen && <div className="scrim" onClick={() => setMenuOpen(false)} aria-hidden="true" />}
      <aside className={`sidebar${menuOpen ? " open" : ""}`} id="main-menu">
        <img src="/peoplecore-logo.svg" alt="PeopleCore" height={30} className="sidebar-logo" />
        <div className="company-chip">
          <span className="company-name">{pick(company.name, company.name_ar)}</span>
          <span className="company-role">{(ws.membership.role ? pick(ws.membership.role.name_en, ws.membership.role.name_ar) : "")}</span>
        </div>
        <nav aria-label={t("navWorkspace")}>
          {me.some((i) => i.show) && (
            <>
              <p className="nav-label">{t("navMe")}</p>
              <ul>{me.filter((i) => i.show).map(navLink)}</ul>
            </>
          )}
          <p className="nav-label">{t("navWorkspace")}</p>
          <ul>{core.filter((i) => i.show).map(navLink)}</ul>
          {moduleItems.length > 0 && (
            <>
              <p className="nav-label">{t("navYourModules")}</p>
              <ul>{moduleItems.map(navLink)}</ul>
            </>
          )}
        </nav>
      </aside>
      <div className="main">
        <header className="topbar">
          <div className="row">
            <button type="button" className="menu-btn" aria-controls="main-menu" aria-expanded={menuOpen}
              aria-label="Menu" onClick={() => setMenuOpen(!menuOpen)}>
              <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true"><path d="M3 5h14M3 10h14M3 15h14" /></svg>
            </button>
            <span className="topbar-title">{visible.find((i) => i.id === active)?.label}</span>
          </div>
          <div className="row">
            <LangSwitch />
            <button type="button" className="btn secondary small" onClick={onSignOut}>
              {t("signOut")}
            </button>
          </div>
        </header>
        <div className="content">{content()}</div>
      </div>
    </div>
  );
}

